import React, { useState, useRef, useEffect, useCallback } from 'react';
import { node, string, number } from 'prop-types';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Icon } from '@fiverr-private/fit';
import { l_chevron_left, l_chevron_right } from '@fiverr-private/icons';
import './style.scss';

const Scroller = ({ children, className, scrollStep, checkOverflowDelay = 50 }) => {
    const [ canScrollLeft, setCanScrollLeft] = useState(false);
    const [ canScrollRight, setCanScrollRight ] = useState(false);
    const [ step, setStep ] = useState(scrollStep);
    const ref = useRef();

    const checkOverflow = useCallback(() => {
        requestAnimationFrame(() => {
            const scrollLeft = ref.current.scrollLeft;
            const scrollView = ref.current.scrollWidth - ref.current.clientWidth;
            const canScrollLeft = scrollLeft > 0;
            const canScrollRight = scrollLeft < scrollView;

            setCanScrollLeft(canScrollLeft);
            setCanScrollRight(canScrollRight);
            if (!step) {
                setStep(ref.current.clientWidth - 50);
            }

        });
    }, [step]);

    useEffect(() => {
        const checkOverflowDebounced = debounce(checkOverflow, checkOverflowDelay);
        const scroller = ref.current;

        scroller && scroller.addEventListener('scroll', checkOverflowDebounced);
        window.addEventListener('resize', checkOverflowDebounced);
        checkOverflow();

        return () => {
            scroller && scroller.removeEventListener('scroll', checkOverflowDebounced);
            window.removeEventListener('resize', checkOverflowDebounced);
        };
    }, [ref, checkOverflow, checkOverflowDelay]);

    const scrollLeft = () => {
        scroll(-step);
    };

    const scrollRight = () => {
        scroll(step);
    };

    const scroll = (delta) => {
        ref.current.scrollTo({
            left: ref.current.scrollLeft + delta,
            behavior: 'smooth'
        });
    };

    const classNames = classnames(
        'dynamic-scrollbar-wrapper',
        className,
        { 'has-overflow': canScrollLeft || canScrollRight }
    );

    return (
        <nav className={classNames}>
            { canScrollLeft &&
                <button className="left" onClick={scrollLeft}>
                    <Icon className="icon-chevron" size={15} color="#7A7D85">{ l_chevron_left }</Icon>
                </button>
            }
            { canScrollRight &&
                <button className="right" onClick={scrollRight}>
                    <Icon className="icon-chevron" size={15} color="#7A7D85">{ l_chevron_right }</Icon>
                </button>
            }
            <ul className="scrolled-list" ref={ref}>
                {children}
            </ul>
        </nav>
    );
};

Scroller.propTypes = {
    children: node,
    className: string,
    scrollStep: number,
    checkOverflowDelay: number
};

export default Scroller;
